import { useEffect, useMemo, useRef, useState } from "react";
import { apiRequest } from "../../shared/api/client";
import type { ContractEditorSeed } from "./contract-types";
import { contractEditorPayload, createContractEditorForm, isContractEditorFormValid } from "./contract-editor-form";
import type { ContractEditorForm } from "./contract-editor-form";
import { useContractEditorDataWorkspace } from "./useContractEditorDataWorkspace";

export function useContractEditorWorkspace({ seed, onSaved }: { seed?: ContractEditorSeed | null; onSaved: (contract: { id: string }) => void }) {
  const editorKey = seed ? `${seed.id}:${seed.currentVersion}` : "new";
  const current = useRef({ key: editorKey, generation: 0 });
  if (current.current.key !== editorKey) current.current = { key: editorKey, generation: current.current.generation + 1 };
  const [form, setForm] = useState(() => createContractEditorForm(seed, new Date()));
  const formOwner = useRef(editorKey);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const locked = useRef(false);
  const mounted = useRef(true);
  const visibleForm = formOwner.current === editorKey ? form : createContractEditorForm(seed, new Date());
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    formOwner.current = editorKey;
    setForm(createContractEditorForm(seed, new Date()));
    setError("");
  }, [editorKey, seed]);
  const { suppliers, documents } = useContractEditorDataWorkspace({ supplierId: visibleForm.supplierId, seedSupplierId: seed?.supplierId, editorKey, setForm, setError });
  const revising = Boolean(seed);
  const valid = useMemo(() => isContractEditorFormValid(visibleForm, revising) && documents.some(item => item.id === visibleForm.signedDocumentId), [visibleForm, revising, documents]);
  async function save() {
    if (locked.current) return;
    if (!valid) { setError("Zorunlu alanları ve limitleri kontrol edin."); return; }
    locked.current = true;
    setBusy(true);
    setError("");
    const generation = current.current.generation;
    const isCurrent = () => mounted.current && generation === current.current.generation;
    try {
      const saved = await apiRequest<{ id: string }>(seed ? `/api/contracts/${seed.id}/revisions` : "/api/contracts", {
        method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(contractEditorPayload(visibleForm, seed)),
      });
      if (isCurrent()) onSaved(saved);
    } catch (caught) {
      if (isCurrent()) setError(caught instanceof Error ? caught.message : "Sözleşme kaydedilemedi.");
    } finally {
      locked.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  const field = <K extends keyof ContractEditorForm>(key: K, value: ContractEditorForm[K]) => setForm(previous => ({ ...previous, [key]: value, ...(key === "supplierId" ? { signedDocumentId: "" } : {}) }));
  function setCategory(value: string) {
    if (value === "Hammadde" || value === "Ambalaj") field("category", value);
  }
  return { revising, suppliers, documents, busy, error, form: visibleForm, valid, save, field, setCategory };
}
